<script>
/**
 * 정산관리 목록 페이지.
 *
 * trade_unified_ledger 테이블 전체(호가 + 상품구매)에 대한 정산 내역을 조회한다.
 * - 매수·매도 메뉴(P2P_ASK만), 인출관리(PRODUCT_BUY만)와 달리 거래유형 구분 없이 전체 표시
 *
 * 기능:
 * - 통합원장 목록 조회 (서버 페이지네이션)
 * - 검색 필터: 기간 / 거래유형(전체/호가/상품구매) / 현물구분(금/은) / 키워드
 * - 행 클릭 → /settlement/:tradeUnifiedLedgerCode 상세 이동
 *
 * 권한:
 * - HQ 관리자: 전체 조회
 * - 지점 관리자: rec_branch_code 또는 use_branch_code = 내 지점코드인 건만 (서버 자동 필터링)
 */
import { getSettlementList } from '@/api/settlement.js'
import AppPagination from '@/components/common/AppPagination.vue'

export default {
  name: 'SettlementListPage',

  components: { AppPagination },

  data() {
    /**
     * 기본 검색 기간 계산.
     * 디폴트: 현재날짜 15일 전 ~ 현재날짜 1달 후
     */
    const toDate = (d) => d.toISOString().substring(0, 10)
    const today = new Date()
    const defaultStart = new Date(today)
    defaultStart.setDate(defaultStart.getDate() - 15)
    const defaultEnd = new Date(today)
    defaultEnd.setMonth(defaultEnd.getMonth() + 1)

    return {
      /* ── 검색 조건 ── */
      search: {
        keyword: '',
        tradeKind: '',
        spotType: '',
        startDate: toDate(defaultStart),
        endDate: toDate(defaultEnd),
      },

      /** 거래유형 select 옵션 */
      tradeKindOptions: [
        { title: '전체', value: '' },
        { title: '호가', value: 'P2P_ASK' },
        { title: '상품구매', value: 'PRODUCT_BUY' },
      ],

      /** 현물구분 select 옵션 */
      spotTypeOptions: [
        { title: '전체', value: '' },
        { title: '금', value: 'GOLD' },
        { title: '은', value: 'SILVER' },
      ],

      /* ── 목록 데이터 ── */
      items: [],

      /* ── 페이지네이션 ── */
      page: 1,
      size: 10,
      total: 0,

      /* ── UI 상태 ── */
      loading: false,
    }
  },

  watch: {
    '$route.query': {
      handler() {
        this.applySearchFromRoute()
        this.fetchList()
      },
      deep: true,
    },
  },

  mounted() {
    this.applySearchFromRoute()
    this.fetchList()
  },

  methods: {
    applySearchFromRoute() {
      const q = this.$route.query || {}
      if (q.startDate) this.search.startDate = String(q.startDate)
      if (q.endDate) this.search.endDate = String(q.endDate)
      if (q.tradeKind !== undefined && q.tradeKind !== null) this.search.tradeKind = String(q.tradeKind)
      if (q.spotType !== undefined && q.spotType !== null) this.search.spotType = String(q.spotType)
      if (q.keyword !== undefined && q.keyword !== null) this.search.keyword = String(q.keyword)
      const p = parseInt(String(q.page || ''), 10)
      if (!Number.isNaN(p) && p > 0) this.page = p
      const s = parseInt(String(q.size || ''), 10)
      if (!Number.isNaN(s) && s > 0) this.size = s
    },
    /**
     * 서버에서 통합원장 정산 목록을 조회한다.
     * 검색 조건 + 페이지네이션 파라미터를 전달하고 결과를 items / total에 세팅한다.
     */
    async fetchList() {
      this.loading = true
      try {
        const res = await getSettlementList({
          tradeKind: this.search.tradeKind || undefined,
          spotType:  this.search.spotType  || undefined,
          keyword:   this.search.keyword   || undefined,
          startDate: this.search.startDate || undefined,
          endDate:   this.search.endDate   || undefined,
          page:      this.page,
          size:      this.size,
        })
        if (res.data?.code === 0) {
          const data = res.data.data
          this.items = data?.items || []
          this.total = data?.total ?? 0
        }
      } catch (e) {
        console.error('[SettlementListPage] fetchList error:', e)
      } finally {
        this.loading = false
      }
    },

    /** 검색 버튼 클릭: 페이지를 1로 리셋 후 재조회 */
    handleSearch() {
      this.page = 1
      this.fetchList()
    },

    /** 초기화 버튼: 검색 조건 + 페이지 초기화 후 재조회 */
    handleReset() {
      const toDate = (d) => d.toISOString().substring(0, 10)
      const today = new Date()
      const defaultStart = new Date(today)
      defaultStart.setDate(defaultStart.getDate() - 15)
      const defaultEnd = new Date(today)
      defaultEnd.setMonth(defaultEnd.getMonth() + 1)
      this.search = {
        keyword:   '',
        tradeKind: '',
        spotType:  '',
        startDate: toDate(defaultStart),
        endDate:   toDate(defaultEnd),
      }
      this.page = 1
      this.fetchList()
    },

    /** 페이지 변경 (AppPagination emit) */
    onPageChange(newPage) {
      this.page = newPage
      this.fetchList()
    },

    /** 페이지당 항목 수 변경 (AppPagination emit) */
    onSizeChange(newSize) {
      this.size = newSize
      this.page = 1
      this.fetchList()
    },

    /** 행 클릭 → 정산관리 상세 페이지 이동 */
    goToDetail(item) {
      this.$router.push(`/settlement/${item.tradeUnifiedLedgerCode}`)
    },

    /**
     * 거래유형에 따른 chip 색상 반환.
     * P2P_ASK=호가(amber), PRODUCT_BUY=상품구매(teal)
     */
    getTradeKindColor(kind) {
      if (kind === 'P2P_ASK')     return 'amber-darken-2'
      if (kind === 'PRODUCT_BUY') return 'teal'
      return 'grey'
    },

    /**
     * 현물구분에 따른 chip 색상 반환.
     * GOLD=금(amber), SILVER=은(grey)
     */
    getSpotTypeColor(spotType) {
      if (spotType === 'GOLD')   return 'amber'
      if (spotType === 'SILVER') return 'grey'
      return 'primary'
    },

    /**
     * 날짜/시간 문자열을 'yyyy-MM-dd HH:mm' 형식으로 포맷한다.
     * null이면 '-' 반환.
     */
    formatDateTime(val) {
      if (!val) return '-'
      return String(val).substring(0, 16).replace('T', ' ')
    },

    /**
     * 날짜 문자열을 'yyyy-MM-dd' 형식으로 포맷한다.
     * null이면 '-' 반환.
     */
    formatDate(val) {
      if (!val) return '-'
      return String(val).substring(0, 10)
    },

    /** 금액을 천단위 콤마 포맷으로 변환. null/빈 값이면 '-' 반환 */
    formatAmount(val) {
      if (val == null || val === '') return '-'
      return Number(val).toLocaleString()
    },

    /**
     * 지점 표시 텍스트 반환.
     * 지점명이 있으면 '지점명 (코드)', 없으면 코드만, 둘 다 없으면 '-'
     */
    formatBranch(code, name) {
      if (!code) return '-'
      return name ? `${name} (${code})` : code
    },
  },
}
</script>

<template>
  <div>
    <!-- 페이지 헤더 -->
    <div class="d-flex align-center justify-space-between mb-1">
      <div>
        <div class="text-h5 font-weight-bold">정산관리</div>
        <div class="text-body-2 text-grey mt-1">통합 거래 원장 정산 내역 조회 (호가·상품구매 전체)</div>
      </div>
    </div>

    <!-- 검색 영역 -->
    <v-card rounded="lg" elevation="1" class="mt-5 mb-5">
      <v-card-item class="py-3 bg-orange-lighten-5">
        <v-card-title class="text-subtitle-2 font-weight-bold">검색 조건</v-card-title>
      </v-card-item>
      <v-card-text class="pt-4">
        <v-row>
          <!-- 기간 시작 -->
          <v-col cols="12" sm="2">
            <v-text-field
              v-model="search.startDate"
              label="시작일"
              type="date"
              variant="outlined"
              density="compact"
              hide-details
            />
          </v-col>
          <!-- 기간 종료 -->
          <v-col cols="12" sm="2">
            <v-text-field
              v-model="search.endDate"
              label="종료일"
              type="date"
              variant="outlined"
              density="compact"
              hide-details
            />
          </v-col>
          <!-- 거래유형 -->
          <v-col cols="12" sm="2">
            <v-select
              v-model="search.tradeKind"
              :items="tradeKindOptions"
              item-title="title"
              item-value="value"
              label="거래유형"
              variant="outlined"
              density="compact"
              hide-details
            />
          </v-col>
          <!-- 현물구분 -->
          <v-col cols="12" sm="2">
            <v-select
              v-model="search.spotType"
              :items="spotTypeOptions"
              item-title="title"
              item-value="value"
              label="현물구분"
              variant="outlined"
              density="compact"
              hide-details
            />
          </v-col>
          <!-- 키워드 -->
          <v-col cols="12" sm="3">
            <v-text-field
              v-model="search.keyword"
              label="키워드"
              placeholder="원장코드, 회원명, 결제코드 등"
              prepend-inner-icon="mdi-magnify"
              variant="outlined"
              density="compact"
              hide-details
              clearable
              @keyup.enter="handleSearch"
            />
          </v-col>
        </v-row>

        <div class="d-flex justify-end ga-2 mt-4 border-t-thin pt-4">
          <v-btn color="primary" prepend-icon="mdi-magnify" @click="handleSearch">검색</v-btn>
          <v-btn variant="outlined" prepend-icon="mdi-refresh" @click="handleReset">초기화</v-btn>
        </div>
      </v-card-text>
    </v-card>

    <!-- 목록 카드 -->
    <v-card rounded="lg" elevation="1">
      <v-card-item class="py-3">
        <v-card-title class="text-subtitle-1 font-weight-bold">
          정산 내역
          <v-chip size="small" color="teal" label class="ml-2">{{ total }}건</v-chip>
        </v-card-title>
      </v-card-item>

      <!-- 로딩 인디케이터 -->
      <v-progress-linear v-if="loading" indeterminate color="teal" />

      <v-table density="compact" class="border-t settlement-table">
        <thead>
          <tr>
            <th class="text-center">번호</th>
            <th class="text-center">원장코드</th>
            <th class="text-center">거래유형</th>
            <th class="text-center">현물</th>
            <th class="text-center">매수회원</th>
            <th class="text-center">매도회원</th>
            <th class="text-right">중량(g)</th>
            <th class="text-right">거래금액</th>
            <th class="text-right">본사수수료</th>
            <th class="text-center">이용지점</th>
            <th class="text-right">이용지점 수수료</th>
            <th class="text-center">추천지점</th>
            <th class="text-right">추천지점 수수료</th>
            <th class="text-center">발생일시</th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="(item, index) in items"
            :key="item.tradeUnifiedLedgerCode"
            class="settlement-row"
            style="cursor: pointer;"
            @click="goToDetail(item)"
          >
            <!-- 번호 (역순) -->
            <td class="text-center text-caption text-grey">
              {{ total - (page - 1) * size - index }}
            </td>
            <!-- 원장코드 -->
            <td class="text-center">
              <span class="text-caption font-weight-medium text-grey-darken-1">
                {{ item.tradeUnifiedLedgerCode || '-' }}
              </span>
            </td>
            <!-- 거래유형 chip -->
            <td class="text-center">
              <v-chip
                :color="getTradeKindColor(item.tradeKind)"
                size="x-small"
                label
                variant="tonal"
                class="font-weight-medium"
              >
                {{ item.tradeKindLabel || item.tradeKind || '-' }}
              </v-chip>
            </td>
            <!-- 현물구분 chip -->
            <td class="text-center">
              <v-chip
                v-if="item.spotType"
                :color="getSpotTypeColor(item.spotType)"
                size="x-small"
                label
                variant="tonal"
                class="font-weight-medium"
              >
                {{ item.spotTypeLabel || item.spotType }}
              </v-chip>
              <span v-else class="text-caption text-grey">-</span>
            </td>
            <!-- 매수회원 -->
            <td class="text-center text-caption font-weight-medium">
              {{ item.buyerName || '-' }}
            </td>
            <!-- 매도회원 -->
            <td class="text-center text-caption font-weight-medium">
              {{ item.sellerName || '-' }}
            </td>
            <!-- 중량 -->
            <td class="text-right text-caption">
              {{ item.weightG != null ? item.weightG : '-' }}
            </td>
            <!-- 거래금액 -->
            <td class="text-right text-caption font-weight-medium">
              {{ formatAmount(item.tradeAmount) }}
            </td>
            <!-- 본사수수료 -->
            <td class="text-right text-caption">
              {{ formatAmount(item.hqFeeAmount) }}
            </td>
            <!-- 이용지점 -->
            <td class="text-center text-caption text-grey-darken-1">
              {{ formatBranch(item.useBranchCode, item.useBranchName) }}
            </td>
            <!-- 이용지점 수수료 -->
            <td class="text-right text-caption">
              {{ formatAmount(item.useBranchFeeAmount) }}
            </td>
            <!-- 추천지점 -->
            <td class="text-center text-caption text-grey-darken-1">
              {{ formatBranch(item.recBranchCode, item.recBranchName) }}
            </td>
            <!-- 추천지점 수수료 -->
            <td class="text-right text-caption">
              {{ formatAmount(item.recBranchFeeAmount) }}
            </td>
            <!-- 발생일시 -->
            <td class="text-center text-caption text-grey-darken-1">
              {{ formatDateTime(item.occurredAt) }}
            </td>
          </tr>

          <!-- 데이터 없음 -->
          <tr v-if="!loading && items.length === 0">
            <td colspan="14" class="text-center text-grey py-10">
              <v-icon size="40" class="d-block mx-auto mb-2" color="grey-lighten-2">
                mdi-cash-register
              </v-icon>
              검색 결과가 없습니다.
            </td>
          </tr>
        </tbody>
      </v-table>

      <!-- 페이지네이션 -->
      <div class="px-4 pb-4">
        <AppPagination
          :total="total"
          :page="page"
          :size="size"
          @update:page="onPageChange"
          @update:size="onSizeChange"
        />
      </div>
    </v-card>
  </div>
</template>

<style scoped>
/* 테이블 행 호버 효과 */
.settlement-row:hover {
  background-color: rgba(0, 150, 136, 0.05);
}

/* 테이블 헤더 고정 스타일 */
.settlement-table th {
  font-size: 0.75rem;
  font-weight: 700;
  white-space: nowrap;
}

/* 테이블 셀 세로 패딩 조정 */
.settlement-table td {
  padding-top: 10px;
  padding-bottom: 10px;
}
</style>
